import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, ChevronRight, Star } from "lucide-react";

// Add these type definitions to match your existing types
interface Feature {
  name?: string;
  en_name?: string;
  disabled?: boolean;
}

interface Plan {
  id: number;
  name: string;
  type: string;
  price?: number;
  features: Feature[];
}

// Define props interface for the component
interface PricingTableProps {
  plans: Plan[];
  calculatePrice: (plan: Plan) => number;
  onChoosePlan: (plan: Plan) => Promise<void>;
  isLoading: { id: number };
  getCombinationId: (plan: Plan) => number;
  mode: string;
}

const PricingTable: React.FC<PricingTableProps> = ({
  plans,
  calculatePrice,
  onChoosePlan,
  isLoading,
  getCombinationId,
  mode,
}) => {
  const { t } = useTranslation("global");
  const [myValue, setMyValue] = useState("");
  const [displayPlans, setDisplayPlans] = useState<Plan[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [popularPlanId, setPopularPlanId] = useState<number | null>(null);

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Get language from localStorage
  useEffect(() => {
    const updateMyValue = () => {
      const storedLangue = localStorage.getItem("langue");
      setMyValue(storedLangue || "");
    };

    updateMyValue();
    const intervalId = setInterval(updateMyValue, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Filter plans based on mode
  useEffect(() => {
    const filteredPlans = plans.filter((plan) => plan.type === mode);
    setDisplayPlans(filteredPlans);
    
    // Set middle plan as popular if there are 3 plans
    if (filteredPlans.length === 3) {
      setPopularPlanId(filteredPlans[1].id);
    } else if (filteredPlans.length > 0) {
      setPopularPlanId(filteredPlans[0].id);
    }
  }, [plans, mode]);

  // Get all unique features across all plans
  const allFeatures = React.useMemo(() => {
    const featureSet = new Set<string>();
    displayPlans.forEach((plan) => {
      plan.features.forEach((feature) => {
        const featureName =
          myValue === "French" ? feature.name : feature.en_name;
        if (featureName) {
          featureSet.add(featureName);
        }
      });
    });
    return Array.from(featureSet);
  }, [displayPlans, myValue]);

  // Check if a plan has a specific feature
  const planHasFeature = (plan: Plan, featureName: string): boolean => {
    return plan.features.some((feature) => {
      const planFeatureName =
        myValue === "French" ? feature.name : feature.en_name;
      return planFeatureName === featureName && !feature.disabled;
    });
  };

  if (displayPlans.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t("pricing.noPlansAvailable")}
      </div>
    );
  }

  // Mobile view - stacked cards
  if (isMobile) {
    return (
      <div className="space-y-6">
        {displayPlans.map((plan) => {
          const isPopular = plan.id === popularPlanId;
          const finalPrice = calculatePrice(plan);

          return (
            <div 
              key={plan.id} 
              className={`relative rounded-xl overflow-hidden shadow-lg ${
                isPopular 
                  ? "border-2 border-green-500" 
                  : "border border-gray-200"
              }`}
            >
              {/* Popular badge */}
              {isPopular && (
                <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center">
                  <Star className="w-3 h-3 mr-1 fill-white" />
                  {t("pricing.popular", "POPULAR")}
                </div>
              )}
              
              {/* Plan header */}
              <div className={`p-6 ${isPopular ? "bg-gradient-to-r from-green-50 to-green-100" : "bg-white"}`}>
                <h3 className="text-xl font-bold text-gray-800">{plan.name}</h3>
                <div className="mt-2 flex items-baseline">
                  <span className="text-3xl font-extrabold text-gray-900">{`€${finalPrice}`}</span>
                </div>
              </div>
              
              {/* Features */}
              <div className="px-6 py-4 bg-white">
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
                  {t("pricing.includedFeatures", "Included features")}
                </h4>
                <ul className="space-y-3">
                  {allFeatures.map((featureName, index) => (
                    <li key={index} className="flex items-start">
                      {planHasFeature(plan, featureName) ? (
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="ml-3 text-sm text-gray-700">{featureName}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Button */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <button
                  disabled={isLoading.id === plan.id}
                  onClick={() => onChoosePlan(plan)}
                  className={`w-full flex justify-center items-center px-5 py-3 rounded-lg text-base font-medium transition-all duration-150 ${
                    isPopular
                      ? "bg-green-500 text-white hover:bg-green-600 shadow-md hover:shadow-lg"
                      : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {isLoading.id !== plan.id ? (
                    <>
                      {t("choose.text")} <span className="ml-1">{plan.name}</span>
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {t("pricing.loading")}
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className="overflow-hidden border border-gray-200 rounded-lg shadow-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            {/* Empty cell for feature column header */}
            <th scope="col" className="w-1/3 px-6 py-6 bg-gray-50"></th>
            
            {/* Plan column headers */}
            {displayPlans.map((plan) => {
              const isPopular = plan.id === popularPlanId;
              const finalPrice = calculatePrice(plan);
              
              return (
                <th 
                  key={plan.id} 
                  scope="col" 
                  className={`px-6 py-6 text-center ${isPopular ? "bg-green-50" : "bg-gray-50"}`}
                >
                  <div className="flex flex-col items-center">
                    {isPopular && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mb-2">
                        <Star className="w-3 h-3 mr-1" />
                        {t("pricing.popular", "POPULAR")}
                      </span>
                    )}
                    <span className={`text-lg font-bold ${isPopular ? "text-green-600" : "text-gray-900"}`}>
                      {plan.name}
                    </span>
                    <span className="mt-2 text-2xl font-extrabold text-gray-900">{`€${finalPrice}`}</span>
                    
                    <button
                      disabled={isLoading.id === plan.id}
                      onClick={() => onChoosePlan(plan)}
                      className={`mt-4 w-full flex justify-center items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                        isPopular
                          ? "bg-green-500 text-white hover:bg-green-600 shadow-md hover:shadow-lg"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {isLoading.id !== plan.id ? (
                        <>
                          {t("choose.text")}
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </>
                      ) : (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {t("pricing.loading")}
                        </>
                      )}
                    </button>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        
        <tbody className="bg-white divide-y divide-gray-200">
          {/* Feature rows */}
          {allFeatures.map((featureName, featureIndex) => (
            <tr key={featureIndex} className={featureIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              {/* Feature name */}
              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                {featureName}
              </td>
              
              {/* Feature availability per plan */}
              {displayPlans.map((plan) => {
                const isPopular = plan.id === popularPlanId;
                return (
                  <td key={plan.id} className="px-6 py-4 text-center">
                    {planHasFeature(plan, featureName) ? (
                      <CheckCircle className={`h-5 w-5 mx-auto ${isPopular ? "text-green-500" : "text-blue-500"}`} />
                    ) : (
                      <XCircle className="h-5 w-5 mx-auto text-gray-300" />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PricingTable;